import { useEffect, useRef } from "react";
import zoomSdk from "@zoom/appssdk";
import LogManager from "../utils/logManager";
import { ZOOM_RUNNING_CONTEXT } from "../constants";

const HEARTBEAT_INTERVAL = 5 * 60 * 1000;
const PONG_TIMEOUT = 10 * 1000;
const MAX_BACKOFF = 30 * 1000;
const MAX_JITTER = 5 * 1000;
const MAX_INITIAL_CONNECT_RETRIES = 5;

/**
 * useWebSocket — manages WebSocket connection lifecycle.
 * Connects when meetingUUID is provided, maintains heartbeat,
 * reconnects on unexpected drops.
 *
 * @param {string|null} meetingUUID - meeting identifier (null = don't connect)
 * @param {function} onMessage - called when a non-pong message arrives
 * @param {object} [options]
 * @param {function} [options.onConnectionChange] - called with the real socket state (true on
 *   open, false on close) so callers can drive connected-state off the actual lifecycle rather
 *   than intent.
 */
export function useWebSocket(meetingUUID, onMessage, options = {}) {
  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const pongTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const backoffRef = useRef(1000);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onConnectionChangeRef = useRef(options.onConnectionChange);
  onConnectionChangeRef.current = options.onConnectionChange;

  useEffect(() => {
    if (!meetingUUID) return;

    let isEffectStale = false;
    let initialConnectFailCount = 0;
    let hasEverConnected = false;

    LogManager.info("[WebSocket] Hook activated, meetingUUID:", meetingUUID);
    backoffRef.current = 1000;

    const clearTimers = () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };

    const scheduleReconnect = () => {
      if (isEffectStale) return;
      const delay = backoffRef.current;
      const jitter = Math.random() * Math.min(delay, MAX_JITTER);
      LogManager.info(`[WebSocket] Reconnecting in ${Math.round(delay + jitter)}ms`);
      reconnectTimeoutRef.current = setTimeout(() => connect(), delay + jitter);
      backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF);
    };

    const connect = () => {
      if (isEffectStale) return;

      const url = `wss://${window.location.host}/ws?meetingUUID=${encodeURIComponent(meetingUUID)}`;
      LogManager.info("[WebSocket] Connecting to:", url);

      const ws = new WebSocket(url);
      wsRef.current = ws;
      let hasOpened = false;

      ws.onopen = () => {
        hasOpened = true;
        hasEverConnected = true;
        initialConnectFailCount = 0;
        backoffRef.current = 1000;
        LogManager.info("[WebSocket] Connected");
        onConnectionChangeRef.current?.(true);

        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: "ping" }));
            pongTimeoutRef.current = setTimeout(() => {
              LogManager.info("[WebSocket] Pong timeout — connection dead");
              ws.close();
            }, PONG_TIMEOUT);
          }
        }, HEARTBEAT_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "pong") {
            clearTimeout(pongTimeoutRef.current);
          } else if (onMessageRef.current) {
            onMessageRef.current(data);
          }
        } catch (err) {
          LogManager.warn("[WebSocket] Failed to parse message:", event.data);
        }
      };

      ws.onclose = async () => {
        clearTimers();
        wsRef.current = null;
        onConnectionChangeRef.current?.(false);
        if (isEffectStale) return;

        /* 
        Connection failed before ever opening (e.g., 429, network down)
        Retry without any check — if user left, the cleanup function
        will run and set isEffectStale=true, stopping further attempts
        */
        if (!hasOpened && !hasEverConnected) {
          initialConnectFailCount++;
          if (initialConnectFailCount >= MAX_INITIAL_CONNECT_RETRIES) {
            LogManager.warn("[WebSocket] Max initial connection retries reached, stopping retries.");
            return;
          }
          LogManager.info("[WebSocket] Initial connection failed, retrying");
          scheduleReconnect();
          return;
        }

        // Connection was open or previously connected — check context
        try {
          const ctx = await zoomSdk.getRunningContext();
          if (isEffectStale) return;
          if (ctx.context === ZOOM_RUNNING_CONTEXT.IN_MEETING) {
            LogManager.info("[WebSocket] Unexpected disconnect, reconnecting");
            scheduleReconnect();
          } else {
            LogManager.info("[WebSocket] User left meeting, not reconnecting");
          }
        } catch (err) {
          if (!isEffectStale) {
            LogManager.info("[WebSocket] Context check failed, attempting reconnect");
            scheduleReconnect();
          }
        }
      };

      ws.onerror = () => {
        LogManager.info("[WebSocket] Connection error");
      };
    };

    // Connect after random jitter
    const jitter = Math.random() * MAX_JITTER;
    LogManager.info(`[WebSocket] Will connect in ${Math.round(jitter)}ms (jitter)`);
    const jitterTimeout = setTimeout(() => connect(), jitter);

    // Cleanup on unmount or meetingUUID change
    return () => {
      isEffectStale = true;
      clearTimeout(jitterTimeout);
      clearTimers();
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [meetingUUID]);
}
